/**
 * Runtime Tenant Configuration System
 * Replaces build-time environment variables with runtime API calls
 * This enables the same frontend image to work for all tenants
 */

export interface TenantBranding {
  tenantId: string;
  title: string;
  logo?: string;
  favicon?: string;
  companyName: string;
  primaryColor: string;
}

export interface TenantHyperswitchConfig {
  publishableKey: string;
  environment: 'sandbox' | 'staging' | 'production';
}

export interface TenantConfig {
  tenantId: string;
  branding: TenantBranding;
  features: {
    enabled: string[];
    tier: 'basic' | 'standard' | 'premium';
  };
  payments: {
    hyperswitch: TenantHyperswitchConfig;
  };
}

// Cache for tenant config to avoid repeated API calls
let tenantConfigCache: TenantConfig | null = null;
let tenantConfigPromise: Promise<TenantConfig> | null = null;

/**
 * Fetch tenant configuration from the API
 * Uses caching to avoid repeated requests
 */
export const fetchTenantConfig = async (options?: { signal?: AbortSignal; strict?: boolean }): Promise<TenantConfig> => {
  // Return cached config if available
  if (tenantConfigCache) {
    return tenantConfigCache;
  }

  // Return existing promise if already fetching
  if (tenantConfigPromise) {
    return tenantConfigPromise;
  }

  // Start fetching config
  tenantConfigPromise = (async () => {
    try {
      console.log('🏥 Fetching tenant configuration from API...');
      
      const response = await fetch('/api/tenant/config', {
        signal: options?.signal,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Don't include auth - this is a public endpoint
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tenant config: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(`API error: ${data.error || 'Unknown error'}`);
      }

      // Transform API response to frontend config format
      const config: TenantConfig = {
        tenantId: data.data.tenantId,
        branding: {
          tenantId: data.data.tenantId,
          title: data.data.branding.title,
          logo: data.data.branding.logo,
          favicon: data.data.branding.favicon,
          companyName: data.data.branding.companyName,
          primaryColor: data.data.branding.primaryColor,
        },
        features: {
          enabled: ['analytics', 'bulk-orders'], // Default features
          tier: 'standard' // Default tier
        },
        payments: {
          hyperswitch: {
            // Hyperswitch publishable key (pk_*), served at runtime so one shared
            // frontend image can carry each tenant's own key.
            publishableKey: data.data.payments?.hyperswitch?.publishableKey || '',
            environment: data.data.payments?.hyperswitch?.environment || 'production',
          }
        }
      };

      // Cache the result
      tenantConfigCache = config;
      
      console.log(`✅ Tenant configuration loaded: ${config.branding.companyName} (${config.tenantId})`);
      
      return config;

    } catch (error) {
      if (options?.strict) throw error;
      console.error('❌ Failed to fetch tenant configuration:', error);
      
      // Return fallback configuration
      const fallbackConfig: TenantConfig = {
        tenantId: 'bloom',
        branding: {
          tenantId: 'bloom',
          title: 'Bloom Health Platform',
          logo: '/5-axis-logo.svg',
          favicon: '/favicon.svg',
          companyName: '5 Axis Health',
          primaryColor: '#3B82F6',
        },
        features: {
          enabled: ['analytics'],
          tier: 'basic'
        },
        payments: {
          hyperswitch: {
            publishableKey: '',
            environment: 'production'
          }
        }
      };

      console.warn('🔄 Using fallback tenant configuration');
      
      // Don't cache fallback config so we can retry later
      return fallbackConfig;
    } finally {
      // Clear the promise so subsequent calls can retry
      tenantConfigPromise = null;
    }
  })();

  return tenantConfigPromise;
};

/**
 * Get current tenant configuration
 * Returns cached config or throws if not loaded
 */
export const getCurrentTenantConfig = (): TenantConfig => {
  if (!tenantConfigCache) {
    throw new Error('Tenant configuration not loaded. Call fetchTenantConfig() first.');
  }
  return tenantConfigCache;
};

/**
 * Get the runtime-fetched Hyperswitch config (publishable key + environment),
 * or null if tenant config hasn't loaded yet. Used by the build-time tenant
 * config to overlay the correct per-tenant key onto payment components.
 */
export const getRuntimeHyperswitchConfig = (): TenantHyperswitchConfig | null => {
  return tenantConfigCache?.payments?.hyperswitch ?? null;
};

/**
 * Initialize tenant configuration at app startup
 * This replaces the old initializeTenantConfig() function
 */
export const initializeTenantConfig = async (): Promise<TenantConfig> => {
  return await fetchTenantConfig();
};

/**
 * Clear cached configuration (useful for testing or tenant switching)
 */
export const clearTenantConfigCache = (): void => {
  tenantConfigCache = null;
  tenantConfigPromise = null;
};

/**
 * Validate hex color format
 */
export const isValidHexColor = (color: string): boolean => {
  return /^#[0-9A-F]{6}$/i.test(color);
};

/**
 * Generate color shades from primary color
 */
export const generateColorShades = (primaryColor: string) => {
  if (!isValidHexColor(primaryColor)) {
    console.warn(`Invalid color format: ${primaryColor}, using default blue`);
    primaryColor = '#3B82F6';
  }

  // Simple color shade generation (could be enhanced with a proper color library)
  const hex = primaryColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Generate lighter shade (add 40 to each component, max 255)
  const lighterR = Math.min(255, r + 40);
  const lighterG = Math.min(255, g + 40);
  const lighterB = Math.min(255, b + 40);
  const lighter = `#${lighterR.toString(16).padStart(2, '0')}${lighterG.toString(16).padStart(2, '0')}${lighterB.toString(16).padStart(2, '0')}`;

  // Generate darker shade (subtract 40 from each component, min 0)
  const darkerR = Math.max(0, r - 40);
  const darkerG = Math.max(0, g - 40);
  const darkerB = Math.max(0, b - 40);
  const darker = `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;

  return {
    primary: primaryColor,
    lighter,
    darker
  };
};

/**
 * Check if a feature is enabled for the current tenant
 */
export const isFeatureEnabled = (feature: string): boolean => {
  try {
    const config = getCurrentTenantConfig();
    return config.features.enabled.includes(feature);
  } catch (error) {
    console.warn('Cannot check feature flag, tenant config not loaded:', error);
    return false;
  }
};

/**
 * Get tenant tier
 */
export const getTenantTier = (): 'basic' | 'standard' | 'premium' => {
  try {
    const config = getCurrentTenantConfig();
    return config.features.tier;
  } catch (error) {
    console.warn('Cannot get tenant tier, tenant config not loaded:', error);
    return 'basic';
  }
};
